import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { TemplateProvider } from "@/context/TemplateContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { SiteContentProvider } from "@/context/SiteContentContext";
import { AdaptiveHeader } from "@/components/layout/AdaptiveHeader";
import { AdaptiveFooter } from "@/components/layout/AdaptiveFooter";
import { CmsThemeProvider } from "@/context/CmsThemeContext";
import { HomePage } from "@/pages/HomePage";
import { AboutPage } from "@/pages/AboutPage";
import { ServicesPage } from "@/pages/ServicesPage";
import { ServiceAreasPage } from "@/pages/ServiceAreasPage";
import { ResourcesPage, ArticlePage } from "@/pages/ResourcesPage";
import { ContactPage } from "@/pages/ContactPage";
import { InteractiveToasts } from "@/components/common/InteractiveToasts";
import { PromoBanner } from "@/components/common/PromoBanner";
import { PageTracker } from "@/components/common/PageTracker";
import { FloatingFormWidget } from "@/components/common/FloatingFormWidget";
import { AIChatWidget } from "@/components/common/AIChatWidget";
import { CookieConsent } from "@/components/common/CookieConsent";
import { AccessibilityWidget } from "@/components/common/AccessibilityWidget";
import { ThemeEffectsProvider } from "@/components/effects";
import React, { Suspense } from "react";

// Lazy-load all admin pages to reduce initial bundle size
const AdminAuthPage = React.lazy(() => import("@/pages/admin/AdminAuthPage").then(m => ({ default: m.AdminAuthPage })));
const AdminDashboard = React.lazy(() => import("@/pages/admin/AdminDashboard").then(m => ({ default: m.AdminDashboard })));
const AdminOverview = React.lazy(() => import("@/pages/admin/AdminOverview").then(m => ({ default: m.AdminOverview })));
const AdminLeads = React.lazy(() => import("@/pages/admin/AdminLeads").then(m => ({ default: m.AdminLeads })));
const AdminPromotions = React.lazy(() => import("@/pages/admin/AdminPromotions").then(m => ({ default: m.AdminPromotions })));
const AdminSettings = React.lazy(() => import("@/pages/admin/AdminSettings").then(m => ({ default: m.AdminSettings })));
const AdminAIUsage = React.lazy(() => import("@/pages/admin/AdminAIUsage").then(m => ({ default: m.AdminAIUsage })));
const AdminBuyCredits = React.lazy(() => import("@/pages/admin/AdminBuyCredits").then(m => ({ default: m.AdminBuyCredits })));
const AdminSetupWizard = React.lazy(() => import("@/pages/admin/AdminSetupWizard").then(m => ({ default: m.AdminSetupWizard })));
const AdminUsers = React.lazy(() => import("@/pages/admin/AdminUsers").then(m => ({ default: m.AdminUsers })));
const AdminPerformance = React.lazy(() => import("@/pages/admin/AdminPerformance").then(m => ({ default: m.AdminPerformance })));
const AdminAccessibility = React.lazy(() => import("@/pages/admin/AdminAccessibility").then(m => ({ default: m.AdminAccessibility })));
const CMSLayout = React.lazy(() => import("@/pages/admin/cms").then(m => ({ default: m.CMSLayout })));
const CMSHero = React.lazy(() => import("@/pages/admin/cms").then(m => ({ default: m.CMSHero })));
const CMSSEO = React.lazy(() => import("@/pages/admin/cms").then(m => ({ default: m.CMSSEO })));
const CMSPages = React.lazy(() => import("@/pages/admin/cms").then(m => ({ default: m.CMSPages })));
const CMSBlog = React.lazy(() => import("@/pages/admin/cms").then(m => ({ default: m.CMSBlog })));
const CMSPrompts = React.lazy(() => import("@/pages/admin/cms").then(m => ({ default: m.CMSPrompts })));
const CMSSections = React.lazy(() => import("@/pages/admin/cms/CMSSections").then(m => ({ default: m.CMSSections })));
const CMSNavigation = React.lazy(() => import("@/pages/admin/cms/CMSNavigation").then(m => ({ default: m.CMSNavigation })));
const CMSJobs = React.lazy(() => import("@/pages/admin/cms/CMSJobs"));
const CMSTeam = React.lazy(() => import("@/pages/admin/cms/CMSTeam"));
const CMSLegal = React.lazy(() => import("@/pages/admin/cms/CMSLegal"));
const CMSServiceLocations = React.lazy(() => import("@/pages/admin/cms/CMSServiceLocations"));
const CMSServicesManagement = React.lazy(() => import("@/pages/admin/cms/CMSServicesManagement"));
const CMSForms = React.lazy(() => import("@/pages/admin/cms/CMSForms"));
const CMSFeaturedProjects = React.lazy(() => import("@/pages/admin/cms/CMSFeaturedProjects"));
const CMSPlans = React.lazy(() => import("@/pages/admin/cms/CMSPlans"));
const CMSEvents = React.lazy(() => import("@/pages/admin/cms/CMSEvents"));
const AdminSuperAdmin = React.lazy(() => import("@/pages/admin/AdminSuperAdmin").then(m => ({ default: m.AdminSuperAdmin })));
const AdminSiteDesign = React.lazy(() => import("@/pages/admin/AdminSiteDesign").then(m => ({ default: m.AdminSiteDesign })));
const AdminClientModules = React.lazy(() => import("@/pages/admin/AdminClientModules").then(m => ({ default: m.AdminClientModules })));
const AdminBilling = React.lazy(() => import("@/pages/admin/AdminBilling").then(m => ({ default: m.AdminBilling })));
const AdminDatabase = React.lazy(() => import("@/pages/admin/AdminDatabase").then(m => ({ default: m.AdminDatabase })));
const AdminIntegrations = React.lazy(() => import("@/pages/admin/AdminIntegrations").then(m => ({ default: m.AdminIntegrations })));
const AdminGEOMonitor = React.lazy(() => import("@/pages/admin/AdminGEOMonitor").then(m => ({ default: m.AdminGEOMonitor })));
const AdminMarketingPlan = React.lazy(() => import("@/pages/admin/AdminMarketingPlan").then(m => ({ default: m.AdminMarketingPlan })));
const AdminSEOPerformance = React.lazy(() => import("@/pages/admin/AdminSEOPerformance").then(m => ({ default: m.AdminSEOPerformance })));
const AdminBrandIntelligence = React.lazy(() => import("@/pages/admin/AdminBrandIntelligence").then(m => ({ default: m.AdminBrandIntelligence })));
const AdminCustomerIntelligence = React.lazy(() => import("@/pages/admin/AdminCustomerIntelligence").then(m => ({ default: m.AdminCustomerIntelligence })));
const AdminLocalIntelligence = React.lazy(() => import("@/pages/admin/AdminLocalIntelligence").then(m => ({ default: m.AdminLocalIntelligence })));
const AdminCompetitorIntelligence = React.lazy(() => import("@/pages/admin/AdminCompetitorIntelligence").then(m => ({ default: m.AdminCompetitorIntelligence })));
const AdminPricingIntelligence = React.lazy(() => import("@/pages/admin/AdminPricingIntelligence").then(m => ({ default: m.AdminPricingIntelligence })));
const AdminReviews = React.lazy(() => import("@/pages/admin/AdminReviews").then(m => ({ default: m.AdminReviews })));
const AdminGBPMarketing = React.lazy(() => import("@/pages/admin/AdminGBPMarketing").then(m => ({ default: m.AdminGBPMarketing })));
const AdminContentMarketing = React.lazy(() => import("@/pages/admin/AdminContentMarketing").then(m => ({ default: m.AdminContentMarketing })));
const AdminLinkChecker = React.lazy(() => import("@/pages/admin/AdminLinkChecker").then(m => ({ default: m.AdminLinkChecker })));
const AdminAnalytics = React.lazy(() => import("@/pages/admin/AdminAnalytics").then(m => ({ default: m.AdminAnalytics })));
const AdminA2PCompliance = React.lazy(() => import("@/pages/admin/AdminA2PCompliance").then(m => ({ default: m.AdminA2PCompliance })));
const AdminMaintenance = React.lazy(() => import("@/pages/admin/AdminMaintenance").then(m => ({ default: m.AdminMaintenance })));
const AdminQueryLogs = React.lazy(() => import("@/pages/admin/AdminQueryLogs").then(m => ({ default: m.AdminQueryLogs })));
const AdminShopifyIntegration = React.lazy(() => import("@/pages/admin/AdminShopifyIntegration").then(m => ({ default: m.AdminShopifyIntegration })));
const AdminBrands = React.lazy(() => import("@/pages/admin/AdminBrands"));
const AdminEcommerce = React.lazy(() => import("@/pages/admin/AdminEcommerce"));
const AdminOnePathAI = React.lazy(() => import("@/pages/admin/AdminOnePathAI").then(m => ({ default: m.AdminOnePathAI })));
const AdminTheBuilderMarket = React.lazy(() => import("@/pages/admin/AdminTheBuilderMarket").then(m => ({ default: m.AdminTheBuilderMarket })));
const AdminTradeRated = React.lazy(() => import("@/pages/admin/AdminTradeRated").then(m => ({ default: m.AdminTradeRated })));
import CareersPage from "@/pages/CareersPage";
import JobDetailPage from "@/pages/JobDetailPage";
import ReviewsPage from "@/pages/ReviewsPage";
import MaintenancePlansPage from "@/pages/MaintenancePlansPage";
import EventsPage from "@/pages/EventsPage";
import EventDetailPage from "@/pages/EventDetailPage";
import DynamicPage from "@/pages/DynamicPage";
import ServiceAreaPage from "@/pages/ServiceAreaPage";
import ServiceRegionPage from "@/pages/ServiceRegionPage";
import ServiceSegmentPage from "@/pages/ServiceSegmentPage";
import ServiceCategoryPage from "@/pages/ServiceCategoryPage";
import ServiceDetailPage from "@/pages/ServiceDetailPage";
import TwoSegmentResolver from "@/pages/TwoSegmentResolver";
import ThreeSegmentResolver from "@/pages/ThreeSegmentResolver";

import BlogPostPage from "@/pages/BlogPostPage";
import SitemapPage from "@/pages/SitemapPage";
import TeamPage from "@/pages/TeamPage";
import ProductsPage from "@/pages/ProductsPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import OffersPage from "@/pages/OffersPage";
import BrandDetailPage from "@/pages/BrandDetailPage";
import ShopPage from "@/pages/ShopPage";
import SharedCartPage from "@/pages/SharedCartPage";
import NotFound from "./pages/NotFound";
import { useEffect, useState } from "react";
import { pmsAuth, pmsFrom } from '@/integrations/pms';

const SuperAdminGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [allowed, setAllowed] = useState<boolean | null>(null);
  useEffect(() => {
    pmsAuth.getSession().then((session) => {
      setAllowed(session?.user?.email?.endsWith('@ciwebgroup.com') ?? false);
    });
  }, []);
  if (allowed === null) return null;
  if (!allowed) return <div className="p-8 text-center text-muted-foreground">Access denied.</div>;
  return <>{children}</>;
};

const queryClient = new QueryClient();

/**
 * Seeds page design configs into react-query cache after the provider tree
 * has mounted (inside a useEffect), so no hooks are called at module level.
 */
const PageDesignPrefetcher: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useEffect(() => {
    pmsFrom('page_design_config')
      .select('*')
      .order('page_type')
      .then(({ data }: { data: any[] | null }) => {
        if (data) {
          data.forEach((row: any) => {
            queryClient.setQueryData(['page-design-config', row.page_type], row);
          });
        }
      })
      .catch(() => {
        /* If the prefetch fails, usePageDesign will fetch individually */
      });
  }, []);

  return <>{children}</>;
};

const App = () => (
  <HelmetProvider>
    <div>
    <QueryClientProvider client={queryClient}>
      <TemplateProvider>
        <ThemeProvider>
          <PageDesignPrefetcher>
          <SiteContentProvider>
            <CmsThemeProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <ReactQueryDevtools initialIsOpen={false} />
              <BrowserRouter>
                <Routes>
                {/* Admin Routes - lazy loaded */}
                  <Route path="/admin/auth" element={<Suspense fallback={null}><AdminAuthPage /></Suspense>} />
                  <Route path="/admin" element={<Suspense fallback={null}><AdminDashboard /></Suspense>}>
                    <Route index element={<AdminOverview />} />
                    <Route path="analytics" element={<AdminAnalytics />} />
                    <Route path="setup" element={<AdminSetupWizard />} />
                    <Route path="cms" element={<CMSLayout />}>
                      <Route index element={<CMSHero />} />
                      <Route path="navigation" element={<CMSNavigation />} />
                      <Route path="sections" element={<CMSSections />} />
                      <Route path="projects" element={<CMSFeaturedProjects />} />
                    </Route>
                    <Route path="seo" element={<CMSSEO />} />
                    <Route path="pages" element={<CMSPages />} />
                    <Route path="posts" element={<CMSBlog />} />
                    <Route path="leads" element={<AdminLeads />} />
                    <Route path="promotions" element={<AdminPromotions />} />
                    <Route path="prompts" element={<CMSPrompts />} />
                    <Route path="jobs" element={<CMSJobs />} />
                    <Route path="team" element={<CMSTeam />} />
                    <Route path="legal" element={<CMSLegal />} />
                    <Route path="service-locations" element={<CMSServiceLocations />} />
                    <Route path="services-mgmt" element={<CMSServicesManagement />} />
                    <Route path="forms" element={<CMSForms />} />
                    <Route path="plans" element={<CMSPlans />} />
                    <Route path="events" element={<CMSEvents />} />
                    <Route path="shopify" element={<AdminShopifyIntegration />} />
                    <Route path="brands" element={<AdminBrands />} />
                    <Route path="ecommerce" element={<AdminEcommerce />} />
                    <Route path="audits" element={<AdminPerformance />} />
                    <Route path="accessibility" element={<AdminAccessibility />} />
                    <Route path="settings" element={<AdminSettings />} />
                    <Route path="ai-usage" element={<AdminAIUsage />} />
                    <Route path="buy-credits" element={<AdminBuyCredits />} />
                    <Route path="users" element={<AdminUsers />} />
                    <Route path="super-admin" element={<SuperAdminGuard><AdminSuperAdmin /></SuperAdminGuard>} />
                    <Route path="site-design" element={<SuperAdminGuard><AdminSiteDesign /></SuperAdminGuard>} />
                    <Route path="client-modules" element={<SuperAdminGuard><AdminClientModules /></SuperAdminGuard>} />
                    <Route path="billing" element={<SuperAdminGuard><AdminBilling /></SuperAdminGuard>} />
                    <Route path="database" element={<SuperAdminGuard><AdminDatabase /></SuperAdminGuard>} />
                    <Route path="integrations" element={<SuperAdminGuard><AdminIntegrations /></SuperAdminGuard>} />
                    <Route path="geo-monitor" element={<AdminGEOMonitor />} />
                    <Route path="marketing-plan" element={<AdminMarketingPlan />} />
                    <Route path="seo-performance" element={<AdminSEOPerformance />} />
                    <Route path="brand-intelligence" element={<AdminBrandIntelligence />} />
                    <Route path="customer-intelligence" element={<AdminCustomerIntelligence />} />
                    <Route path="local-intelligence" element={<AdminLocalIntelligence />} />
                    <Route path="competitor-intelligence" element={<AdminCompetitorIntelligence />} />
                    <Route path="pricing-intelligence" element={<AdminPricingIntelligence />} />
                    <Route path="reviews" element={<AdminReviews />} />
                    <Route path="gbp-marketing" element={<AdminGBPMarketing />} />
                    <Route path="content-marketing" element={<AdminContentMarketing />} />
                    <Route path="link-checker" element={<AdminLinkChecker />} />
                    <Route path="a2p-compliance" element={<AdminA2PCompliance />} />
                    <Route path="maintenance" element={<SuperAdminGuard><AdminMaintenance /></SuperAdminGuard>} />
                    <Route path="query-logs" element={<AdminQueryLogs />} />
                    <Route path="onepathai" element={<AdminOnePathAI />} />
                    <Route path="thebuildermarket" element={<AdminTheBuilderMarket />} />
                    <Route path="traderated" element={<AdminTradeRated />} />
                  </Route>

                  {/* Public Routes with Theme Effects */}
                  <Route path="/*" element={
                    <ThemeEffectsProvider>
                    <div className="min-h-screen bg-background text-foreground font-body relative">
                      <AdaptiveHeader />
                      <InteractiveToasts />
                      <PromoBanner variant="floating" />
                      <PageTracker />
                      <FloatingFormWidget />
                      <AIChatWidget />
                      <main className="relative z-10">
                          <Routes>
                            <Route path="/" element={<HomePage />} />
                            <Route path="/about" element={<AboutPage />} />
                            <Route path="/services" element={<ServicesPage />} />
                            
                            <Route path="/:a/:b/:c" element={<ThreeSegmentResolver />} />
                            <Route path="/service-areas" element={<ServiceAreasPage />} />
                            <Route path="/:a/:b" element={<TwoSegmentResolver />} />
                            <Route path="/blog/:slug" element={<BlogPostPage />} />
                            <Route path="/resources" element={<ResourcesPage />} />
                            <Route path="/resources/:articleId" element={<ArticlePage />} />
                            <Route path="/contact" element={<ContactPage />} />
                            <Route path="/careers" element={<CareersPage />} />
                            <Route path="/careers/:jobSlug" element={<JobDetailPage />} />
                            <Route path="/promos" element={<OffersPage />} />
                            <Route path="/brands/:slug" element={<BrandDetailPage />} />
                            <Route path="/reviews" element={<ReviewsPage />} />
                            <Route path="/maintenance-plans" element={<MaintenancePlansPage />} />
                            <Route path="/events" element={<EventsPage />} />
                            <Route path="/events/:slug" element={<EventDetailPage />} />
                            <Route path="/team" element={<TeamPage />} />
                            <Route path="/products" element={<ProductsPage />} />
                            <Route path="/products/:slug" element={<ProductDetailPage />} />
                            <Route path="/shop" element={<ShopPage />} />
                            <Route path="/shop/shared-cart/:token" element={<SharedCartPage />} />
                            <Route path="/shop/wishlist/:token" element={<SharedCartPage />} />
                            <Route path="/sitemap" element={<SitemapPage />} />
                            <Route path="/:slug" element={<ServiceSegmentPage />} />
                            <Route path="*" element={<NotFound />} />
                          </Routes>
                        </main>
                        <AdaptiveFooter />
                        <CookieConsent />
                        <AccessibilityWidget />
                      </div>
                    </ThemeEffectsProvider>
                  } />
                </Routes>
              </BrowserRouter>
            </TooltipProvider>
            </CmsThemeProvider>
          </SiteContentProvider>
          </PageDesignPrefetcher>
        </ThemeProvider>
      </TemplateProvider>
    </QueryClientProvider>
    </div>
  </HelmetProvider>
);

export default App;
